import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { Toaster } from './components/ui/sonner';
import { isAuthenticated } from './lib/auth';
import { isAdminAuthenticated } from './lib/adminAuth';
import { isEmployeeAuthenticated } from './lib/employeeAuth';
import { isClientAuthenticated } from './lib/clientAuth';
import { useDynamicSEO } from './lib/useDynamicSEO';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SwUpdateToast from './components/SwUpdateToast';
import ErrorBoundary from './components/ErrorBoundary';
import { useLocation } from 'react-router-dom';
import EmployeeAuthCallback from './pages/employee/EmployeeAuthCallback';
import GirgitHost from './components/mascot/GirgitHost';

/**
 * Google-Auth hash gate — dispatches to the right callback view based on
 * the current pathname so employee/career flows never cross-wire.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
function GoogleAuthHashGate({ children }) {
  const location = useLocation();
  const hash = location.hash || '';
  const isEmergentCallback =
    hash.startsWith('#session_id=') || hash.startsWith('#/?session_id=');
  if (typeof window !== 'undefined' && isEmergentCallback) {
    if (location.pathname.startsWith('/employee/')) {
      return <EmployeeAuthCallback />;
    }
    return <CareerAuthCallback />;
  }
  return children;
}

// Pages
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import RoleSelectionPage from './pages/RoleSelectionPage';
import RoleDetailPage from './pages/RoleDetailPage';
import ApplyPage from './pages/ApplyPage';
import DashboardPage from './pages/DashboardPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import OnboardingPage from './pages/OnboardingPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import NewAdminDashboard from './pages/admin/NewAdminDashboard';

// Employee Pages
import EmployeeLoginPage from './pages/employee/EmployeeLoginPage';
import CompleteProfilePage from './pages/employee/CompleteProfilePage';
import NewEmployeeDashboard from './pages/employee/NewEmployeeDashboard';
import ClientsPage from './pages/employee/ClientsPage';
import ClientDetailPage from './pages/employee/ClientDetailPage';
import DepartmentsPage from './pages/employee/DepartmentsPage';
import DepartmentDetailPage from './pages/employee/DepartmentDetailPage';
import TeamPage from './pages/employee/TeamPage';
import TeamMemberDetailPage from './pages/employee/TeamMemberDetailPage';
import ReportsPage from './pages/employee/ReportsPage';
import ProfilePage from './pages/employee/ProfilePage';
import LeavesPage from './pages/employee/LeavesPage';
import HelpDeskPage from './pages/employee/HelpDeskPage';
import SettingsPage from './pages/employee/SettingsPage';
import DocumentsPage from './pages/employee/DocumentsPage';
import EmployeeAccountingPage from './pages/employee/AccountingPage';

// Client Pages
import ClientLoginPage from './pages/client/ClientLoginPage';
import NewClientDashboard from './pages/client/NewClientDashboard';

// Career Pages
import CareerLoginPage from './pages/career/CareerLoginPage';
import CareerDashboard from './pages/career/CareerDashboard';
import CareerAuthCallback from './pages/career/CareerAuthCallback';
import CareersPublicPage from './pages/career/CareersPublicPage';
import SignDocumentPage from './pages/SignDocumentPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/auth" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  return !isAuthenticated() ? children : <Navigate to="/dashboard" replace />;
};

// Admin Protected Route Component
const AdminProtectedRoute = ({ children }) => {
  return isAdminAuthenticated() ? children : <Navigate to="/admin/login" replace />;
};

// Admin Public Route Component
const AdminPublicRoute = ({ children }) => {
  return !isAdminAuthenticated() ? children : <Navigate to="/admin/dashboard" replace />;
};

// Employee Protected Route Component
const EmployeeProtectedRoute = ({ children }) => {
  return isEmployeeAuthenticated() ? children : <Navigate to="/employee/login" replace />;
};

// Employee Public Route Component
const EmployeePublicRoute = ({ children }) => {
  return !isEmployeeAuthenticated() ? children : <Navigate to="/employee/dashboard" replace />;
};

// Client Protected Route Component
const ClientProtectedRoute = ({ children }) => {
  return isClientAuthenticated() ? children : <Navigate to="/client/login" replace />;
};

// Client Public Route Component
const ClientPublicRoute = ({ children }) => {
  return !isClientAuthenticated() ? children : <Navigate to="/client/dashboard" replace />;
};

export default function App() {
  useDynamicSEO();  // Load SEO dynamically
  
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <ViewModeProvider>
      <Router>
        <GoogleAuthHashGate>
        <div className="App min-h-screen bg-[#F7F6F9] dark:bg-[#111111] transition-colors duration-300">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            
          <Route
            path="/auth"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/roles"
            element={
              <ProtectedRoute>
                <RoleSelectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/:roleId"
            element={
              <ProtectedRoute>
                <RoleDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apply/:roleId"
            element={
              <ProtectedRoute>
                <ApplyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apply"
            element={
              <ProtectedRoute>
                <ApplyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/:id"
            element={
              <ProtectedRoute>
                <ApplicationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/:applicationId"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminPublicRoute><AdminLoginPage /></AdminPublicRoute>} />
          <Route path="/admin/dashboard" element={<AdminProtectedRoute><ErrorBoundary><NewAdminDashboard /></ErrorBoundary></AdminProtectedRoute>} />

          {/* Employee Routes */}
          <Route path="/employee/login" element={<EmployeePublicRoute><EmployeeLoginPage /></EmployeePublicRoute>} />
          <Route path="/employee/complete-profile" element={<EmployeeProtectedRoute><ErrorBoundary><CompleteProfilePage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/dashboard" element={<EmployeeProtectedRoute><ErrorBoundary><NewEmployeeDashboard /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/clients" element={<EmployeeProtectedRoute><ErrorBoundary><ClientsPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/clients/:clientId" element={<EmployeeProtectedRoute><ErrorBoundary><ClientDetailPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/departments" element={<EmployeeProtectedRoute><ErrorBoundary><DepartmentsPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/departments/:departmentName" element={<EmployeeProtectedRoute><ErrorBoundary><DepartmentDetailPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/team" element={<EmployeeProtectedRoute><ErrorBoundary><TeamPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/team/:employeeId" element={<EmployeeProtectedRoute><ErrorBoundary><TeamMemberDetailPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/reports" element={<EmployeeProtectedRoute><ErrorBoundary><ReportsPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/profile" element={<EmployeeProtectedRoute><ErrorBoundary><ProfilePage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/documents" element={<EmployeeProtectedRoute><ErrorBoundary><DocumentsPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/leaves" element={<EmployeeProtectedRoute><ErrorBoundary><LeavesPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/helpdesk" element={<EmployeeProtectedRoute><ErrorBoundary><HelpDeskPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/settings" element={<EmployeeProtectedRoute><ErrorBoundary><SettingsPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          <Route path="/employee/accounting" element={<EmployeeProtectedRoute><ErrorBoundary><EmployeeAccountingPage /></ErrorBoundary></EmployeeProtectedRoute>} />
          
          {/* Employee Dashboard Subpage Redirects - These routes redirect to the dashboard with appropriate query params */}
          <Route path="/employee/tasks" element={<EmployeeProtectedRoute><Navigate to="/employee/dashboard?page=tasks" replace /></EmployeeProtectedRoute>} />
          <Route path="/employee/calendars" element={<EmployeeProtectedRoute><Navigate to="/employee/dashboard?page=calendars" replace /></EmployeeProtectedRoute>} />
          <Route path="/employee/organisation" element={<EmployeeProtectedRoute><Navigate to="/employee/dashboard?page=organisation" replace /></EmployeeProtectedRoute>} />

          {/* Client Routes */}
          <Route path="/client/login" element={<ClientPublicRoute><ClientLoginPage /></ClientPublicRoute>} />
          <Route path="/client/dashboard" element={<ClientProtectedRoute><ErrorBoundary><NewClientDashboard /></ErrorBoundary></ClientProtectedRoute>} />

          {/* Career Portal Routes */}
          <Route path="/careers" element={<CareersPublicPage />} />
          <Route path="/sign/:token" element={<SignDocumentPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<PrivacyPolicyPage doc="terms" />} />
          <Route path="/terms-of-use" element={<PrivacyPolicyPage doc="terms" />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/careers/role/:roleId" element={<CareersPublicPage />} />
          <Route path="/career/login" element={<CareerLoginPage />} />
          <Route path="/career/auth/callback" element={<CareerAuthCallback />} />
          <Route path="/career/dashboard" element={<ErrorBoundary><CareerDashboard /></ErrorBoundary>} />

          <Route path="/employee/auth/callback" element={<EmployeeAuthCallback />} />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
        <SwUpdateToast />
        <PWAInstallPrompt />
        <GirgitHost />
      </div>
      </GoogleAuthHashGate>
    </Router>
    </ViewModeProvider>
    </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}